import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../shared/interfaces/JobStatus";
import type { Job } from "../../store/jobStore";
import type { JobStatusSnapshot } from "../../features/jobs/api/jobStatusApi";

export type GenerationView =
  | { kind: "loading" }
  | {
      kind: "progress";
      progress: number;
      message?: string;
      videoTitles?: string[];
      videoCount?: number;
    }
  | { kind: "awaiting-input"; targetId: string }
  | { kind: "ready"; reportId: string }
  | { kind: "failed"; message: string; canRetry: boolean }
  | { kind: "unknown" };

const RUNNING_STATUSES = new Set<string>([
  ReportJobStatus.PENDING,
  ReportJobStatus.STARTED,
  ReportJobStatus.GENERATING,
  ReportJobStatus.EXTRACTING,
]);

/**
 * Pure view derivation for the generation surface. The /jobStatus snapshot
 * is authoritative for status/progress; the (per-device) registry entry only
 * enriches the view (video titles, retryability) — a job started on another
 * device still renders read-only from the snapshot alone.
 */
export function deriveGenerationView(
  registryJob: Job | undefined,
  snapshot: JobStatusSnapshot | undefined,
  error: unknown,
): GenerationView {
  // A hard HTTP error is authoritative even when a previous snapshot is
  // retained in the cache (react-query keeps data through error dispatches);
  // without this the page would show eternal progress off the stale snapshot.
  const hardHttpError = !!error && isAxiosError(error) && !!error.response;
  if (!snapshot || hardHttpError) {
    if (error) {
      if (isAxiosError(error) && error.response) {
        // The server doesn't know this job (or rejected it): a stale link or
        // another device's registry — unless WE know it failed locally.
        if (registryJob?.status === ReportJobStatus.FAILED) {
          return {
            kind: "failed",
            message: registryJob.error || "Video processing failed.",
            canRetry: true,
          };
        }
        return { kind: "unknown" };
      }
      // Network blip: keep showing what we know; the poll rides through.
      if (registryJob) {
        return progressView(registryJob.progress, registryJob);
      }
      return { kind: "loading" };
    }
    // No snapshot yet. A locally-FAILED job has no active query to deliver
    // one (the fan-out only polls active jobs), so derive from the registry.
    if (registryJob?.status === ReportJobStatus.FAILED) {
      return {
        kind: "failed",
        message: registryJob.error || "Video processing failed.",
        canRetry: true,
      };
    }
    if (registryJob?.status === ReportJobStatus.PENDING_REVIEW) {
      return registryJob.reportId
        ? { kind: "ready", reportId: registryJob.reportId }
        : { kind: "loading" };
    }
    return { kind: "loading" };
  }

  const status = snapshot.jobStatus;
  if (status === ReportJobStatus.AWAITING_INPUT) {
    return {
      kind: "awaiting-input",
      targetId: snapshot.reportId ?? registryJob?.reportId ?? snapshot.jobId,
    };
  }
  if (status === ReportJobStatus.PENDING_REVIEW) {
    const reportId = snapshot.reportId ?? registryJob?.reportId;
    return reportId ? { kind: "ready", reportId } : { kind: "loading" };
  }
  if (status === ReportJobStatus.FAILED) {
    return {
      kind: "failed",
      message:
        snapshot.message ||
        registryJob?.error ||
        "Video processing failed.",
      canRetry: !!registryJob,
    };
  }
  if (RUNNING_STATUSES.has(status)) {
    return progressView(snapshot.progress ?? registryJob?.progress ?? 0, registryJob, snapshot.message);
  }
  // Unknown status: render it as progress rather than an error — matches the
  // poll predicate, which keeps polling unknown statuses.
  return progressView(snapshot.progress ?? registryJob?.progress ?? 0, registryJob, snapshot.message);
}

function progressView(
  progress: number,
  registryJob: Job | undefined,
  message?: string,
): GenerationView {
  return {
    kind: "progress",
    progress,
    message: message || registryJob?.progressMessage,
    videoTitles: registryJob?.videoTitles,
    videoCount: registryJob?.evidenceIDs.length,
  };
}
